import { useState } from 'react';
import type { RoleName } from './roleCapabilities';

/**
 * Lets the capability panel show a role *before* it is chosen.
 *
 * A MUI `Select` only fires `onChange` on commit, so without this the panel
 * could answer "what may this role do?" only after the admin had already
 * assigned the role — the wrong order for a question asked to decide.
 * Hovering (or keyboard-focusing) an option previews it; leaving the list or
 * closing the menu falls back to the current selection.
 */
export function useRolePreview(selected: RoleName) {
  const [preview, setPreview] = useState<RoleName | null>(null);

  return {
    /** Role the panel renders: the hovered candidate, else the committed one. */
    displayRole: preview ?? selected,
    /** Drop the preview — on menu close and when the pointer leaves the list. */
    clearPreview: () => setPreview(null),
    /** Spread onto each MenuItem. Pointer and keyboard both drive the preview. */
    previewProps: (role: RoleName) => ({
      onMouseEnter: () => setPreview(role),
      onFocus: () => setPreview(role),
    }),
  };
}
